import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  getAllCompaniesWithStats,
  createCompany,
  toggleCompanyActive,
  createCompanyAdmin,
  updateCompanyContactEmail,
  CompanyWithStats,
  Company,
} from '../../services/adminService';
import { dpsTheme } from '../../styles/dpsTheme';
import { useDeviceSize } from '../../hooks/useMediaQuery';

// ── Modal générique ───────────────────────────────────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'rgb(15,23,42)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: '16px', padding: '32px', width: '480px', maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: dpsTheme.colors.text.muted }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: '8px',
  border: `1px solid ${dpsTheme.colors.border.primary}`,
  background: 'rgba(30,41,59,0.8)', color: dpsTheme.colors.text.primary,
  fontSize: dpsTheme.typography.fontSize.sm, boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 24px', borderRadius: '8px', border: 'none',
  background: dpsTheme.colors.accent.blue.gradient, color: 'white',
  fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold,
  cursor: 'pointer',
};

// ── Stat pill dans chaque carte ───────────────────────────────────────────────
function StatPill({ icon, value, label, accent }: { icon: React.ReactNode; value: number | string; label: string; accent?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '20px', background: accent ? `${accent}12` : 'rgba(255,255,255,0.04)', border: `1px solid ${accent ? `${accent}30` : dpsTheme.colors.border.secondary}` }}>
      <span style={{ color: accent ?? dpsTheme.colors.text.muted, display: 'flex' }}>{icon}</span>
      <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: accent ?? dpsTheme.colors.text.primary, fontSize: dpsTheme.typography.fontSize.sm }}>{value}</span>
      <span style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.xs }}>{label}</span>
    </div>
  );
}

export function SuperAdminDashboard() {
  const { userProfile } = useAuth();
  const { isMobile } = useDeviceSize();

  const [companies, setCompanies] = useState<CompanyWithStats[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal nouvelle entreprise
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', slug: '' });
  const [creatingCompany, setCreatingCompany] = useState(false);

  // Modal admin par entreprise
  const [adminTarget, setAdminTarget] = useState<Company | null>(null);
  const [adminForm, setAdminForm] = useState({ email: '', firstName: '', lastName: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  const loadCompanies = async () => {
    try {
      setCompanies(await getAllCompaniesWithStats());
    } catch (e: any) {
      toast.error(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCompanies(); }, []);

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyForm.name || !companyForm.slug) {
      toast.error('Nom et slug requis');
      return;
    }
    try {
      setCreatingCompany(true);
      await createCompany(companyForm.name, companyForm.slug.toLowerCase().replace(/\s+/g, '-'));
      toast.success('Entreprise cliente créée');
      setShowCompanyModal(false);
      setCompanyForm({ name: '', slug: '' });
      await loadCompanies();
    } catch (e: any) {
      toast.error(e.message || 'Erreur création entreprise');
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleToggleActive = async (company: CompanyWithStats) => {
    try {
      await toggleCompanyActive(company.id, !company.is_active);
      toast.success(company.is_active ? `${company.name} désactivée` : `${company.name} activée`);
      await loadCompanies();
    } catch (e: any) {
      toast.error(e.message || 'Erreur');
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminTarget || !adminForm.email || !adminForm.firstName || !adminForm.lastName) {
      toast.error('Tous les champs sont requis');
      return;
    }
    try {
      setCreatingAdmin(true);
      await createCompanyAdmin(adminForm.email, adminForm.firstName, adminForm.lastName, adminTarget.id);
      updateCompanyContactEmail(adminTarget.id, adminForm.email).catch(console.error);
      toast.success('Invitation envoyée — l\'admin recevra un email pour définir son mot de passe');
      setAdminTarget(null);
      setAdminForm({ email: '', firstName: '', lastName: '' });
      await loadCompanies();
    } catch (e: any) {
      toast.error(e.message || 'Erreur création admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const activeCompanies = companies.filter(c => c.is_active);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: dpsTheme.colors.text.muted }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: dpsTheme.typography.fontFamily, minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', color: dpsTheme.colors.text.secondary }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? `${dpsTheme.spacing.lg} ${dpsTheme.spacing.md}` : dpsTheme.spacing['3xl'] }}>

        {/* Header */}
        <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', borderRadius: '20px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', marginBottom: '12px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span style={{ fontSize: dpsTheme.typography.fontSize.xs, fontWeight: dpsTheme.typography.fontWeight.semibold, color: '#f87171', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Super Admin</span>
          </div>
          <h1 style={{ fontSize: isMobile ? dpsTheme.typography.fontSize['2xl'] : dpsTheme.typography.fontSize['4xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary, marginBottom: '8px' }}>
            Gestion globale
          </h1>
          <p style={{ color: dpsTheme.colors.text.muted }}>{userProfile?.email} · Accès total</p>
        </div>

        {/* Stats globales */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: dpsTheme.spacing.lg, marginBottom: dpsTheme.spacing['2xl'] }}>
          {[
            { label: 'Entreprises clientes actives', value: activeCompanies.length },
            { label: 'Entreprises clientes total', value: companies.length },
            { label: 'Entreprises clientes inactives', value: companies.length - activeCompanies.length },
          ].map(stat => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing.xl, textAlign: 'center' }}>
              <p style={{ fontSize: dpsTheme.typography.fontSize['3xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>{stat.value}</p>
              <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted, marginTop: '4px' }}>{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Liste des entreprises clientes */}
        <div style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing['2xl'] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: dpsTheme.spacing.xl }}>
            <h2 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
              Entreprises clientes ({companies.length})
            </h2>
            <button onClick={() => setShowCompanyModal(true)} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Nouvelle entreprise cliente
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {companies.map(company => (
              <div
                key={company.id}
                style={{
                  background: dpsTheme.colors.background.darker,
                  border: `1px solid ${dpsTheme.colors.border.secondary}`,
                  borderRadius: dpsTheme.borderRadius.md,
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {/* Ligne principale */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', flexShrink: 0, borderRadius: '8px', background: company.is_active ? 'rgba(59,130,246,0.2)' : 'rgba(100,116,139,0.2)', border: `1px solid ${company.is_active ? 'rgba(59,130,246,0.3)' : 'rgba(100,116,139,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={company.is_active ? '#60a5fa' : '#64748b'} strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                    </div>
                    <div>
                      <p style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>{company.name}</p>
                      <p style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted }}>
                        {company.slug} · Créée le {new Date(company.created_at).toLocaleDateString('fr-FR')}
                      </p>
                      {/* Contact admin */}
                      {company.stats.adminEmail ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.text.muted} strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          <span style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted }}>
                            {company.stats.adminName && <span style={{ color: dpsTheme.colors.text.secondary }}>{company.stats.adminName} — </span>}
                            <a
                              href={`mailto:${company.stats.adminEmail}`}
                              style={{ color: '#60a5fa', textDecoration: 'none' }}
                              onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                              onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                            >
                              {company.stats.adminEmail}
                            </a>
                          </span>
                        </div>
                      ) : (
                        <p style={{ fontSize: dpsTheme.typography.fontSize.xs, color: 'rgba(239,68,68,0.6)', marginTop: '4px' }}>
                          Aucun admin configuré
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: dpsTheme.typography.fontSize.xs, fontWeight: dpsTheme.typography.fontWeight.semibold, background: company.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: company.is_active ? '#10b981' : '#ef4444', border: `1px solid ${company.is_active ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>

                    <button
                      onClick={() => { setAdminTarget(company); setAdminForm({ email: '', firstName: '', lastName: '' }); }}
                      style={{ padding: '6px 12px', borderRadius: '6px', fontSize: dpsTheme.typography.fontSize.xs, fontWeight: 500, cursor: 'pointer', border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(139,92,246,0.1)', color: '#a78bfa' }}
                    >
                      + Admin
                    </button>

                    <button
                      onClick={() => handleToggleActive(company)}
                      style={{ padding: '6px 12px', borderRadius: '6px', fontSize: dpsTheme.typography.fontSize.xs, fontWeight: 500, cursor: 'pointer', border: `1px solid ${company.is_active ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`, background: company.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: company.is_active ? '#ef4444' : '#10b981' }}
                    >
                      {company.is_active ? 'Désactiver' : 'Activer'}
                    </button>
                  </div>
                </div>

                {/* Ligne stats */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingTop: '8px', borderTop: `1px solid ${dpsTheme.colors.border.secondary}` }}>
                  <StatPill
                    icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                    value={company.stats.advisorCount}
                    label={company.stats.advisorCount === 1 ? 'conseiller' : 'conseillers'}
                  />
                  <StatPill
                    icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                    value={company.stats.diagnosticCount}
                    label={company.stats.diagnosticCount === 1 ? 'diagnostic' : 'diagnostics'}
                  />
                  <StatPill
                    icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
                    value={company.stats.activeClientCount}
                    label={company.stats.activeClientCount === 1 ? 'client actif' : 'clients actifs'}
                  />
                  {company.stats.totalRevenue > 0 && (
                    <StatPill
                      icon={<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
                      value={`${company.stats.totalRevenue.toLocaleString('fr-FR')} €`}
                      label="CA généré"
                      accent="#10b981"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal nouvelle entreprise cliente */}
      {showCompanyModal && (
        <Modal title="Nouvelle entreprise cliente" onClose={() => setShowCompanyModal(false)}>
          <form onSubmit={handleCreateCompany} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Nom de l'entreprise</label>
              <input style={inputStyle} value={companyForm.name} onChange={e => setCompanyForm(f => ({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }))} placeholder="Sécurité Plus" required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Slug (URL)</label>
              <input style={inputStyle} value={companyForm.slug} onChange={e => setCompanyForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} placeholder="securite-plus" required />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button type="button" onClick={() => setShowCompanyModal(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'transparent', color: dpsTheme.colors.text.secondary, cursor: 'pointer', fontSize: dpsTheme.typography.fontSize.sm }}>Annuler</button>
              <button type="submit" disabled={creatingCompany} style={{ ...btnPrimary, opacity: creatingCompany ? 0.6 : 1 }}>{creatingCompany ? 'Création...' : 'Créer l\'entreprise'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal créer admin */}
      {adminTarget && (
        <Modal title={`Créer un admin — ${adminTarget.name}`} onClose={() => setAdminTarget(null)}>
          <form onSubmit={handleCreateAdmin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Prénom</label>
                <input style={inputStyle} value={adminForm.firstName} onChange={e => setAdminForm(f => ({ ...f, firstName: e.target.value }))} placeholder="Marie" required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Nom</label>
                <input style={inputStyle} value={adminForm.lastName} onChange={e => setAdminForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Martin" required />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Email</label>
              <input style={inputStyle} type="email" value={adminForm.email} onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))} placeholder="marie.martin@entreprise.com" required />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button type="button" onClick={() => setAdminTarget(null)} style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'transparent', color: dpsTheme.colors.text.secondary, cursor: 'pointer', fontSize: dpsTheme.typography.fontSize.sm }}>Annuler</button>
              <button type="submit" disabled={creatingAdmin} style={{ ...btnPrimary, opacity: creatingAdmin ? 0.6 : 1 }}>{creatingAdmin ? 'Envoi...' : 'Envoyer l\'invitation'}</button>
            </div>
            <p style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, textAlign: 'center', marginTop: '4px' }}>
              Un email d'invitation sera envoyé à cette adresse.
            </p>
          </form>
        </Modal>
      )}
    </div>
  );
}
